import React, { useEffect, useState } from 'react';
import {
  Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { apiClient } from '../config/api';

/**
 * Price impact around a single short report.
 *
 * The series is INDEXED TO 100 at the publication-day close. That is what lets
 * one component serve a $0.40 microcap and a $340 large cap: raw dollars are
 * incomparable across the corpus, "fell to 5" and "ran to 298" are not.
 *
 * Resolves by ticker + date rather than report id, because the report detail
 * route is activist/slug and loads its body straight from S3 — it never sees a
 * database id, but it already carries both of these in the query string.
 */

const HORIZON_LABEL = { '1d': '1 day', '1w': '1 week', '1m': '1 month',
                        '3m': '3 months', '6m': '6 months', '1y': '1 year' };

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const delta = p.index - 100;
  return (
    <div className="bg-white border border-gray-300 rounded shadow-lg px-3 py-2 text-xs">
      <div className="font-mono font-semibold text-gray-900">
        {p.index.toFixed(1)}
        <span className={`ml-2 ${delta < 0 ? 'text-red-600' : 'text-green-700'}`}>
          {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
        </span>
      </div>
      <div className="text-gray-500 mt-0.5">{fmtDate(p.date)}</div>
      <div className="text-gray-400">
        close {p.close?.toFixed(2)} · {p.phase === 'before' ? 'pre-report' : 'post-report'}
      </div>
    </div>
  );
};

const EventWindowChart = ({ ticker, date, reportId, before = 30, after = 180 }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!reportId && (!ticker || !date)) { setState('unavailable'); return; }
      try {
        setState('loading');
        const url = reportId
          ? `/api/price-impact/report/${reportId}`
          : `/api/price-impact/lookup?ticker=${encodeURIComponent(ticker)}&date=${encodeURIComponent(date)}`;
        const res = await apiClient.get(url, { params: { before, after } });
        if (cancelled) return;
        setData(res.data);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        // A 404 here is an expected state, not a failure: the price backfill
        // covers US tickers targeted in 2026 and nothing else yet. Say so,
        // rather than showing a broken chart or a scary error.
        setState(e?.response?.status === 404 ? 'unavailable' : 'error');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [ticker, date, reportId, before, after]);

  if (state === 'loading') {
    return (
      <div className="animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-56 mb-3" />
        <div className="h-64 bg-gray-100 rounded" />
      </div>
    );
  }

  if (state === 'unavailable') {
    return (
      <div className="border border-dashed border-gray-300 rounded p-6 text-sm text-gray-500">
        <div className="font-medium text-gray-700 mb-1">No price history yet</div>
        Price data currently covers US-listed targets of reports published in 2026.
        This one is outside that set.
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="border border-red-200 bg-red-50 rounded p-4 text-sm text-red-700">
        Could not load price history. Try reloading the page.
      </div>
    );
  }

  const { report, series, anchors } = data;
  const pubIdx = series.findIndex((p) => p.phase === 'after');
  const pubDate = pubIdx >= 0 ? series[pubIdx].date : null;
  const last = series[series.length - 1];
  const lastDelta = last.index - 100;

  const lo = Math.min(100, ...series.map((p) => p.index));
  const hi = Math.max(100, ...series.map((p) => p.index));
  const pad = (hi - lo) * 0.1 || 5;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h3 className="text-base font-semibold text-gray-900">Price impact</h3>
        <span className="text-xs font-mono text-gray-500">
          indexed to 100 at publication · {report.currency || 'USD'}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {report.ticker} · {report.seller} · published {fmtDate(report.publication_date)}
      </p>

      {/* Anchor chips: the frozen returns, from the same entry price as the
          chart's base, so the two can never disagree. */}
      {anchors?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {anchors.map((a) => (
            <span key={a.horizon}
              className={`px-2 py-0.5 rounded text-xs font-mono border ${
                a.return_pct < 0
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-800'}`}
              title={`${HORIZON_LABEL[a.horizon]} after publication`}>
              {a.horizon} {a.return_pct >= 0 ? '+' : ''}{a.return_pct.toFixed(1)}%
            </span>
          ))}
        </div>
      )}

      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <AreaChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
            <defs>
              <linearGradient id="ewFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2a78d6" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#2a78d6" stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#eceae5" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={48}
                   tick={{ fontSize: 10, fill: '#6f6e6a' }} stroke="#c9c8c2" />
            <YAxis domain={[lo - pad, hi + pad]} width={44}
                   tick={{ fontSize: 10, fill: '#6f6e6a' }} stroke="#c9c8c2" />
            <Tooltip content={<ChartTooltip />} />
            {/* 100 is the entry level: above it the short is losing. */}
            <ReferenceLine y={100} stroke="#c9c8c2" strokeDasharray="3 3" />
            {pubDate && (
              <ReferenceLine x={pubDate} stroke="#52514e" strokeWidth={1.5}
                label={{ value: 'report', position: 'insideTopRight',
                         fontSize: 10, fill: '#52514e' }} />
            )}
            <Area type="monotone" dataKey="index" stroke="#2a78d6" strokeWidth={2}
                  fill="url(#ewFill)" dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        {series.length} trading days ·{' '}
        <span className={lastDelta < 0 ? 'text-green-700' : 'text-red-700'}>
          {lastDelta >= 0 ? '+' : ''}{lastDelta.toFixed(1)}%
        </span>{' '}
        by {fmtDate(last.date)}
      </p>
    </div>
  );
};

export default EventWindowChart;
